import type { DemoLiveFleet, DemoLiveUser } from '@cobliteam/api-lib/src/demoLive/demoLiveSession';
import {
  beginDemoLiveLogin,
  captureDemoLiveSession,
  exitDemoLive,
  getDemoLiveExpiresAt,
  getDemoLiveFleetId,
  getDemoLiveFleets,
  getDemoLiveUser,
  hasRealAccessTokenStored,
  isDemoLiveActive,
  isDemoLivePending,
  isDemoLiveTokenExpired,
  logDemoLive,
  switchDemoLiveFleet,
} from '@cobliteam/api-lib/src/demoLive/demoLiveSession';
import { useEffect, useState } from 'react';

const FLOATING_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: 16,
  right: 16,
  zIndex: 99999,
  fontFamily: 'system-ui, sans-serif',
};

/** Fleets the parallel session can reach; the current one is checked, the others switch on click. */
const FleetList = ({ fleets, currentFleetId, busy, onSelect }: { fleets: DemoLiveFleet[]; currentFleetId: string | null; busy: boolean; onSelect: (fleetId: string) => void }) => {
  if (fleets.length === 0) {
    return <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Não foi possível listar as frotas acessíveis.</div>;
  }

  return (
    <div style={{ maxHeight: 168, overflowY: 'auto', marginBottom: 8 }}>
      {fleets.map((fleet) => {
        const isCurrent = fleet.id === currentFleetId;
        return (
          <button
            key={fleet.id}
            type='button'
            disabled={busy}
            onClick={() => onSelect(fleet.id)}
            aria-current={isCurrent}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              textAlign: 'left',
              background: isCurrent ? '#eaf7ee' : 'transparent',
              color: '#222',
              border: 'none',
              borderRadius: 6,
              padding: '6px 8px',
              fontSize: 12,
              fontWeight: isCurrent ? 600 : 400,
              cursor: busy ? 'progress' : 'pointer',
            }}
          >
            <span aria-hidden style={{ color: '#0f9d58', width: 10 }}>
              {isCurrent ? '✓' : ''}
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fleet.name}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Parallel-session state as displayed by the pill and the menu.
 *
 * `active` and `pending` are mutually exclusive; both false means plain demo.
 */
interface LiveSnapshot {
  active: boolean;
  pending: boolean;
  user: DemoLiveUser | null;
  fleets: DemoLiveFleet[];
  fleetId: string | null;
  /** Real JWT expired: a refresh is attempted on demand, but until then IA and Metabase 401. */
  expired: boolean;
  expiresAt: Date | null;
}

const readLiveSnapshot = (): LiveSnapshot => ({
  active: isDemoLiveActive(),
  pending: isDemoLivePending(),
  user: getDemoLiveUser(),
  fleets: getDemoLiveFleets(),
  fleetId: getDemoLiveFleetId(),
  expired: isDemoLiveTokenExpired(),
  expiresAt: getDemoLiveExpiresAt(),
});

/**
 * Identity of everything the pill and the menu actually render, so a re-read that changed nothing
 * keeps the previous object and skips the re-render. Cheaper and more honest than comparing `Date`
 * instances, which are never referentially equal across reads.
 */
const signatureOf = (live: LiveSnapshot): string =>
  [live.active, live.pending, live.expired, live.fleetId ?? '', live.expiresAt?.getTime() ?? 0, live.fleets.length, live.user?.email ?? ''].join('|');

/**
 * How often the snapshot is re-read. Expiry is a function of wall-clock time with nothing to
 * subscribe to, so polling is the mechanism; the interval only has to be short enough that the label
 * is right by the time someone opens the menu, and the click re-reads anyway.
 */
const SNAPSHOT_POLL_MS = 15_000;

/** Label and color for the parallel-session state. Kept outside the component to keep the menu simple. */
const statusOf = (live: LiveSnapshot): { label: string; color: string } => {
  if (live.active) return live.expired ? { label: 'expirada', color: '#d93025' } : { label: 'ativa', color: '#0f9d58' };
  if (live.pending) return { label: 'aguardando login', color: '#e8a33d' };
  return { label: 'inativa', color: '#bbb' };
};

/**
 * The active parallel session panel: the fleet in focus, the token's validity and the fleet switcher.
 */
const LiveSessionPanel = ({ live, busy, onSwitchFleet }: { live: LiveSnapshot; busy: boolean; onSwitchFleet: (fleetId: string) => void }) => {
  // The fleet list may have failed to load (live mode stays functional in that case), so fall back
  // to the id and, failing that, to a generic label.
  const currentFleetName = live.fleets.find((fleet) => fleet.id === live.fleetId)?.name ?? live.fleetId ?? 'frota não identificada';

  return (
    <>
      {/* The parallel fleet, highlighted — it is what decides what IA and Metabase answer. */}
      <div style={{ background: '#eaf7ee', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: '#0b7a45', marginBottom: 2 }}>Frota paralela</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#222', wordBreak: 'break-word' }}>{currentFleetName}</div>
        {live.user && <div style={{ fontSize: 11, color: '#666', marginTop: 2, wordBreak: 'break-all' }}>{live.user.email}</div>}
        {live.expiresAt && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Token válido até {live.expiresAt.toLocaleString('pt-BR')}</div>}
      </div>
      {live.expired ? (
        <div style={{ fontSize: 12, color: '#a52714', background: '#fdecea', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
          O token desta sessão expirou. A renovação é tentada automaticamente na próxima requisição; se a IA ou o Metabase continuarem falhando, faça logout e entre de novo.
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
          Cobli IA e dashboards do Metabase respondem por esta frota. Todo o resto do demo — veículos, motoristas, viagens, alertas, mapa — continua fictício.
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>Trocar de frota</div>
      <FleetList fleets={live.fleets} currentFleetId={live.fleetId} busy={busy} onSelect={onSwitchFleet} />
    </>
  );
};

interface DemoMenuProps {
  live: LiveSnapshot;
  busy: boolean;
  onReset: () => void;
  onEnterLive: () => void;
  onExitLive: () => void;
  onCancelLive: () => void;
  onSwitchFleet: (fleetId: string) => void;
}

/**
 * The popover's content. Three mutually exclusive parallel-session states — active, awaiting login,
 * inactive — each with its own action. Extracted from `DemoBanner` so the component above stays just
 * the pill plus the handlers.
 */
const DemoMenu = ({ live, busy, onReset, onEnterLive, onExitLive, onCancelLive, onSwitchFleet }: DemoMenuProps) => {
  const status = statusOf(live);

  const actionStyle: React.CSSProperties = {
    width: '100%',
    background: '#f5f5f5',
    color: '#222',
    border: '1px solid #ddd',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 13,
    cursor: busy ? 'progress' : 'pointer',
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 36,
        right: 0,
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: 12,
        minWidth: 260,
        maxWidth: 320,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      }}
    >
      {/* Parallel-session state always on top: it is the first thing you want when opening the menu. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 11, fontWeight: 600, color: '#555' }}>
        <span aria-hidden style={{ color: status.color, fontSize: 14, lineHeight: 1 }}>
          &#9679;
        </span>
        Sessão paralela: {status.label}
      </div>

      {live.active ? (
        <>
          <LiveSessionPanel live={live} busy={busy} onSwitchFleet={onSwitchFleet} />
          <button type='button' onClick={onExitLive} disabled={busy} style={{ ...actionStyle, marginBottom: 6 }}>
            Fazer logout da conta real
          </button>
        </>
      ) : live.pending ? (
        <>
          <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
            Conclua o login na tela de login para ativar a sessão paralela. Enquanto isso o demo fica sem sessão fictícia.
          </div>
          <button type='button' onClick={onCancelLive} disabled={busy} style={{ ...actionStyle, marginBottom: 6 }}>
            Cancelar e voltar ao demo
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>Todos os dados são fictícios e processados localmente.</div>
          <button type='button' onClick={onEnterLive} disabled={busy} style={{ ...actionStyle, marginBottom: 6 }}>
            Entrar com conta real
          </button>
        </>
      )}
      <button type='button' onClick={onReset} disabled={busy} style={actionStyle}>
        Resetar dados do demo
      </button>
    </div>
  );
};

/**
 * Floating "Demo Mode" pill shown only when REACT_APP_DEMO_MODE === 'true'.
 *
 * Two controls: "reset demo data", which clears the MSW store plus tokens and reloads, and "live
 * mode", which authenticates for real so Cobli IA and the Metabase dashboards talk to the backend
 * while everything else stays mocked (see `libs/api-lib/src/demoLive/demoLiveSession.ts`). The active
 * real fleet is displayed and switched here on purpose: the app's own fleet switcher keeps showing
 * the fictional fleet, because the shell's session is still the demo's.
 *
 * Deliberately simple — no design-system imports so it works even if the
 * demo build has cosmetic mismatches in some deep component. This is the
 * salesperson's escape hatch.
 */
const DemoBanner = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isDemo = process.env.REACT_APP_DEMO_MODE === 'true';

  // Kept in state rather than read during render so rendering stays pure — but re-read, never a
  // single mount-time snapshot. Two things change it with no reload and no user action: the clock
  // (`expired` is `exp` vs. `Date.now()`) and the background refresh in `refreshDemoLiveSession`,
  // which re-mints the token from the AI transport's 401 retry. This component is a sibling of
  // `<Router/>` in `Dashboard.tsx`, so it survives every navigation — a snapshot taken at boot would
  // still claim "válido até …" hours after the token died.
  const [live, setLive] = useState(readLiveSnapshot);

  useEffect(() => {
    if (!isDemo) return;

    const sync = () => {
      const next = readLiveSnapshot();
      setLive((previous) => (signatureOf(previous) === signatureOf(next) ? previous : next));
    };

    // On focus too: a tab left open in the background gets throttled timers, and coming back to it is
    // exactly when the answer has to be right.
    sync();
    const intervalId = setInterval(sync, SNAPSHOT_POLL_MS);
    window.addEventListener('focus', sync);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', sync);
    };
  }, [isDemo]);

  /**
   * The bridge between the real login and live mode, not derived state: the login screen writes the
   * real JWT into the standard auth keys and navigates back into the app without going through a new
   * boot. We capture the session into the live-mode keys and reload — at boot `startDemoWorker` seeds
   * the demo tokens back, and that is what hands the shell its fictional identity.
   *
   * Why poll instead of checking once at mount: this component is a **sibling of `<Router/>`** in
   * `Dashboard.tsx`, not a child. It does not unmount on navigation, so the login happens without
   * remounting the banner and without reloading the page. A mount-only check runs while no token
   * exists yet and never again — the token shows up later with nobody watching, leaving the state
   * stuck at `pending`. The watch only exists while `pending` and stops at the first capture.
   */
  useEffect(() => {
    if (!(isDemo && isDemoLivePending())) return;

    logDemoLive('aguardando o login real concluir — vigiando o aparecimento do JWT real.');

    let capturing = false;
    let stopped = false;

    const captureWhenReady = () => {
      if (capturing || stopped || !hasRealAccessTokenStored()) return;
      capturing = true;
      logDemoLive('login real concluído — capturando a sessão para as chaves do modo real e recarregando.');
      void captureDemoLiveSession().then((captured) => {
        if (stopped) return;
        if (captured) {
          window.location.reload();
          return;
        }
        // A real JWT is present and the capture still refused: that is a structural failure (blocked
        // localStorage, for one), not a matter of timing. Retrying would only loop the log.
        stopped = true;
        logDemoLive('captura recusada apesar de haver um JWT real — vigilância encerrada. O estado segue pending.');
      });
    };

    captureWhenReady();
    const intervalId = setInterval(captureWhenReady, 1000);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [isDemo]);

  if (!isDemo) return null;

  const handleReset = async () => {
    if (!confirm('Resetar dados de demonstração? Qualquer alteração feita nesta sessão será perdida.')) return;
    try {
      const module = await import('@cobliteam/msw-demo');
      if (module.resetDemo && typeof module.resetDemo === 'function') {
        module.resetDemo();
      } else {
        throw new Error('resetDemo function not found in @cobliteam/msw-demo');
      }
    } catch (err) {
      const message = 'Erro ao resetar dados do demo';
      console.error(message, err);
      alert(message);
    }
  };

  /**
   * Sends the user to the real login screen instead of asking for credentials here: `modules/login`
   * already handles the whole Cognito flow, including the e-mail MFA challenge and remember-device.
   */
  const handleEnterLive = () => {
    if (!confirm('Entrar com uma conta real? A Cobli IA e os dashboards do Metabase passam a usar dados reais da frota escolhida; todo o resto do demo continua fictício.')) return;
    beginDemoLiveLogin();
    window.location.assign('/login');
  };

  const handleExitLive = () => {
    if (!confirm('Fazer logout da conta real? A Cobli IA e o Metabase voltam a ser mockados. Os dados do demo não são afetados.')) return;
    setBusy(true);
    // Only ends the real session; the demo data stays as is (resetting is the other button).
    exitDemoLive();
    window.location.reload();
  };

  /**
   * The way out of `pending`, for when the login is abandoned.
   *
   * Without this the demo becomes unreachable: `beginDemoLiveLogin` deleted the fake tokens and boot
   * does not seed them while `pending`, so the app is stuck at `/login`. Clearing the state restores
   * the seeding on the next boot.
   */
  const handleCancelLive = () => {
    setBusy(true);
    exitDemoLive();
    window.location.assign('/');
  };

  const handleSwitchFleet = async (fleetId: string) => {
    if (fleetId === live.fleetId) return;
    setBusy(true);
    try {
      await switchDemoLiveFleet(fleetId);
      window.location.reload();
    } catch (err) {
      console.error('Erro ao trocar a frota da sessão real', err);
      alert('Não foi possível trocar a frota da sessão real.');
      setBusy(false);
    }
  };

  return (
    <div style={FLOATING_STYLE}>
      <button
        type='button'
        // Re-read on click as well as on the interval: opening the menu is the one moment the label
        // has to be current, and waiting up to SNAPSHOT_POLL_MS for the next tick is avoidable.
        onClick={() => {
          setLive(readLiveSnapshot());
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        style={{
          background: live.active ? (live.expired ? '#d93025' : '#0f9d58') : live.pending ? '#e8a33d' : '#7a3df5',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        {live.active ? (live.expired ? 'DEMO · SESSÃO EXPIRADA' : 'DEMO + DADOS REAIS') : live.pending ? 'DEMO · AGUARDANDO LOGIN' : 'DEMO MODE'}
      </button>
      {open && (
        <DemoMenu
          live={live}
          busy={busy}
          onReset={handleReset}
          onEnterLive={handleEnterLive}
          onExitLive={handleExitLive}
          onCancelLive={handleCancelLive}
          onSwitchFleet={handleSwitchFleet}
        />
      )}
    </div>
  );
};

export default DemoBanner;
